import { Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { client } from '@/api/client'
import { AuthLayout } from '@/components/AuthLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface SetNewPasswordForm {
  password: string
  passwordConfirm: string
}

export function SetNewPasswordPage() {
  const { t } = useTranslation('auth')
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)
  const [tokenError, setTokenError] = useState('')
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<SetNewPasswordForm>()

  useEffect(() => {
    if (token) {
      validateToken()
    } else {
      navigate('/reset-password')
    }
  }, [token])

  const validateToken = async () => {
    try {
      const response = await client.post('/auth/verify-reset-token/', { token })
      setTokenValid(response.data.success)
      if (!response.data.success) {
        setTokenError(response.data.message)
      }
    } catch (err: any) {
      setTokenValid(false)
      const errorMessage = err.response?.data?.message || ''
      if (errorMessage.includes('expired') || errorMessage.includes('already been used')) {
        setTokenError(t('resetPassword.linkExpired'))
      } else {
        setTokenError(t('setNewPassword.invalidToken'))
      }
    }
  }

  const onSubmit = async (data: SetNewPasswordForm) => {
    setIsSubmitting(true)
    setError('')
    setPasswordError('')

    try {
      const response = await client.post('/auth/reset-password/', {
        token,
        password: data.password,
        password_confirm: data.passwordConfirm
      })

      if (response.data.success) {
        setSuccess(true)
      } else {
        setError(response.data.message || t('setNewPassword.error'))
      }
    } catch (err: any) {
      if (err.response?.data?.password) {
        if (Array.isArray(err.response.data.password) &&
            err.response.data.password.some((msg: string) => msg.includes('This password is too common'))) {
          setPasswordError(t('register.passwordTooCommon'))
        } else {
          setPasswordError(Array.isArray(err.response.data.password) ? err.response.data.password[0] : err.response.data.password)
        }
      } else {
        setError(err.response?.data?.message || t('setNewPassword.error'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (tokenValid === null) {
    return (
      <AuthLayout>
        <div className="flex justify-center mb-8">
          <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
        </div>
        <Card>
          <CardContent className="py-8 text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">{t('setNewPassword.validating')}</p>
          </CardContent>
        </Card>
      </AuthLayout>
    )
  }

  // Invalid token
  if (tokenValid === false) {
    return (
      <AuthLayout>
        <div className="flex justify-center mb-8">
          <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
        </div>
        <Card>
          <CardContent className="py-8 text-center space-y-4">
            <div className="rounded-full bg-destructive/10 p-3 w-fit mx-auto">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <p className="text-destructive font-medium">{tokenError || t('setNewPassword.invalidToken')}</p>
            <Button asChild>
              <Link to="/reset-password">{t('setNewPassword.requestNewLink')}</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    )
  }

  // Success
  if (success) {
    return (
      <AuthLayout>
        <div className="flex justify-center mb-8">
          <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
        </div>
        <Card>
          <CardContent className="py-8 text-center space-y-4">
            <div className="rounded-full bg-emerald-100 p-3 w-fit mx-auto">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold">{t('setNewPassword.successTitle')}</h2>
            <p className="text-muted-foreground text-sm">{t('setNewPassword.successMessage')}</p>
            <Button asChild>
              <Link to="/login">{t('setNewPassword.loginNow')}</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    )
  }

  // Main form
  return (
    <AuthLayout>
      <div className="flex justify-center mb-8">
        <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
      </div>

      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('setNewPassword.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password-input">{t('setNewPassword.passwordLabel')}</Label>
              <div className="relative">
                <Input
                  id="password-input"
                  {...register('password', {
                    required: t('setNewPassword.passwordRequired'),
                    minLength: { value: 8, message: t('register.passwordMinLength') },
                    pattern: { value: /^(?=.*[A-Za-z])(?=.*\d).+$/, message: t('register.passwordComplexity') }
                  })}
                  type={showPassword ? "text" : "password"}
                  placeholder={t('setNewPassword.passwordPlaceholder')}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
              {passwordError && <p className="text-xs text-destructive">{passwordError}</p>}
              <div className="p-2 bg-muted rounded-md text-xs text-muted-foreground">
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>{t('register.passwordMinLength')}</li>
                  <li>{t('register.passwordComplexity')}</li>
                  <li>{t('register.passwordNotCommon')}</li>
                  <li>{t('register.passwordLatinOnly')}</li>
                  <li>{t('register.passwordNoEmail')}</li>
                </ul>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password-confirm-input">{t('setNewPassword.confirmPasswordLabel')}</Label>
              <Input
                id="password-confirm-input"
                {...register('passwordConfirm', {
                  required: t('setNewPassword.confirmPasswordRequired'),
                  validate: value => value === getValues('password') || t('setNewPassword.passwordMismatch')
                })}
                type="password"
                placeholder={t('setNewPassword.confirmPasswordPlaceholder')}
              />
              {errors.passwordConfirm && <p className="text-xs text-destructive">{errors.passwordConfirm.message}</p>}
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('setNewPassword.submitting') : t('setNewPassword.submitButton')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
